import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useState, useEffect } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../../../../../convex/_generated/api";
import { Id } from "../../../../../../convex/_generated/dataModel";
import { useToast } from "@/components/ui/use-toast";

interface ScheduleVisitSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onVisitScheduled?: () => void;
  locationId: Id<"serviceLocations">;
  businessId: Id<"businesses">;
  locationName: string;
  defaultAddress?: string;
}

export function ScheduleVisitSheet({
  isOpen,
  onClose,
  onVisitScheduled,
  locationId,
  businessId,
  locationName,
  defaultAddress,
}: ScheduleVisitSheetProps) {
  // State for form fields
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("10:00");
  const [isAllDay, setIsAllDay] = useState(false);
  const [eventType, setEventType] = useState<string>("walkthrough");
  const [assignedStaff, setAssignedStaff] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  // Fetch organization members
  const members = useQuery(api.calendar.getOrgMembers) || [];

  // Create event mutation
  const createEvent = useMutation(api.calendar.createEvent);

  // Reset form when sheet opens
  useEffect(() => {
    if (isOpen) {
      setTitle("");
      setDescription("");
      setStartDate("");
      setEndDate("");
      setStartTime("09:00");
      setEndTime("10:00");
      setIsAllDay(false);
      setEventType("walkthrough");
      setAssignedStaff([]);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  // Handle form submission
  const handleSubmit = async () => {
    if (!title || !startDate || !startTime || !endTime || assignedStaff.length === 0) {
      toast({
        title: "Error",
        description: "Please fill in all required fields and assign at least one staff member",
        variant: "destructive",
      });
      return;
    }

    // Filter out any invalid staff IDs
    const validStaffIds = assignedStaff.filter(
      (id) => id && id !== "undefined" && typeof id === "string"
    );

    if (validStaffIds.length === 0) {
      toast({
        title: "Error",
        description: "Please assign at least one valid staff member",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await createEvent({
        title,
        description,
        startDate,
        endDate: endDate || startDate,
        startTime,
        endTime,
        isAllDay,
        eventType: eventType as any,
        businessId,
        serviceLocationId: locationId,
        location: locationName,
        address: defaultAddress || "",
        assignedStaff: validStaffIds as Id<"users">[],
      });

      toast({
        title: "Success!",
        description: "Visit scheduled successfully",
      });
      onVisitScheduled?.();
      onClose();
    } catch (error) {
      console.error("Error creating visit:", error);
      toast({
        title: "Error",
        description: "Failed to schedule visit",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle staff assignment toggle
  const toggleStaffAssignment = (memberId: string) => {
    // Ensure memberId is valid before adding to the array
    if (!memberId || memberId === "undefined") {
      console.error("Invalid member ID:", memberId);
      return;
    }

    setAssignedStaff((prev) =>
      prev.includes(memberId) ? prev.filter((id) => id !== memberId) : [...prev, memberId]
    );
  };

  // Handle close
  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent className="w-[500px] overflow-y-auto sm:max-w-[500px]">
        <SheetHeader>
          <SheetTitle>Schedule Visit</SheetTitle>
          <SheetDescription>Create a new visit for this location.</SheetDescription>
        </SheetHeader>
        <div className="space-y-6 py-6">
          <div className="space-y-2">
            <Label htmlFor="title">Visit Title</Label>
            <Input
              id="title"
              placeholder="Enter visit title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              placeholder="Enter visit description (optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="eventType">Visit Type</Label>
            <Select value={eventType} onValueChange={setEventType}>
              <SelectTrigger>
                <SelectValue placeholder="Select visit type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="walkthrough">Walkthrough</SelectItem>
                <SelectItem value="service">Service Visit</SelectItem>
                <SelectItem value="maintenance">Maintenance</SelectItem>
                <SelectItem value="meeting">Meeting</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="startDate">Start Date</Label>
              <Input
                id="startDate"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="endDate">End Date</Label>
              <Input
                id="endDate"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="isAllDay"
              checked={isAllDay}
              onCheckedChange={(checked) => setIsAllDay(checked as boolean)}
            />
            <Label htmlFor="isAllDay">All day event</Label>
          </div>

          {!isAllDay && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startTime">Start Time</Label>
                <Input
                  id="startTime"
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="endTime">End Time</Label>
                <Input
                  id="endTime"
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                />
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label>Assigned Staff</Label>
            <div className="max-h-32 space-y-2 overflow-y-auto rounded-md border p-2">
              {members
                .filter((member: any) => member._id && member._id !== "undefined")
                .map((member: any) => (
                  <div key={member._id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`staff-${member._id}`}
                      checked={assignedStaff.includes(member._id)}
                      onCheckedChange={() => toggleStaffAssignment(member._id)}
                    />
                    <Label htmlFor={`staff-${member._id}`} className="text-sm">
                      {member.name}
                    </Label>
                  </div>
                ))}
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              variant="outline"
              onClick={handleClose}
              className="flex-1"
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              className="flex-1"
              disabled={
                isSubmitting ||
                !title ||
                !startDate ||
                !startTime ||
                !endTime ||
                assignedStaff.length === 0
              }
            >
              {isSubmitting ? "Scheduling..." : "Schedule Visit"}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
